/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/it";
import "dayjs/locale/en";
import { selectAuthUser, selectCounts } from "../../store/selectors";
import { getCounts } from "../../store/actions/auth";
import PropTypes from "prop-types";

const ACCENT = "#3B82F6";
const ACCENT2 = "#6366f1";

const StatBox = ({ label, value, isDark }) => (
  <Box
    sx={{
      flex: 1,
      textAlign: "center",
      py: 1.5,
      px: 1,
      borderRadius: 2,
      background: isDark
        ? "rgba(59,130,246,0.12)"
        : "rgba(59,130,246,0.07)",
    }}
  >
    <Typography
      variant="h3"
      fontWeight={800}
      lineHeight={1}
      sx={{ color: ACCENT, mb: 0.5 }}
    >
      {value ?? 0}
    </Typography>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", fontSize: "0.68rem", lineHeight: 1.3 }}
    >
      {label}
    </Typography>
  </Box>
);

StatBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  isDark: PropTypes.bool,
};

const WelcomeSplash = ({ onDismiss }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const authUser = useSelector(selectAuthUser);
  const counts = useSelector(selectCounts);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    dispatch(getCounts());
  }, [dispatch]);

  const isDark = theme.palette.mode === "dark";
  const lang = i18n.language?.startsWith("it") ? "it" : "en";

  const todayFormatted = (() => {
    const d = dayjs().locale(lang);
    const datePart = d.format("dddd, D MMMM YYYY");
    return datePart.charAt(0).toUpperCase() + datePart.slice(1);
  })();

  return (
    <AnimatePresence onExitComplete={onDismiss}>
      {visible && (
        <motion.div
          key="splash-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: "rgba(0,0,0,0.62)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <motion.div
            key="splash-card"
            initial={{ scale: 0.82, opacity: 0, y: 32 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.82, opacity: 0, y: 32 }}
            transition={{ type: "spring", stiffness: 300, damping: 24, delay: 0.05 }}
            style={{ width: "100%", maxWidth: 440 }}
          >
            <Box
              sx={{
                background: isDark ? theme.palette.background.paper : "#fff",
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              }}
            >
              {/* Gradient header banner */}
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
                  pt: 3,
                  pb: 2.5,
                  px: 4,
                  textAlign: "center",
                  position: "relative",
                }}
              >
                <img
                  src={`/img/logo.png`}
                  alt="HSE Genius"
                  style={{
                    height: 32,
                    marginBottom: 14,
                    filter: "brightness(0) invert(1)",
                    opacity: 0.92,
                  }}
                />
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ color: "#fff", lineHeight: 1.15 }}
                >
                  {t("welcome.greeting", { name: authUser?.first_name || "" })}
                </Typography>
              </Box>

              {/* Body */}
              <Box sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 2.5, textAlign: "center" }}>
                <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                  <StatBox
                    label={t("welcome.stat.imported")}
                    value={counts?.created_counter ?? 0}
                    isDark={isDark}
                  />
                  <StatBox
                    label={t("welcome.stat.validated")}
                    value={counts?.validated_counter ?? 0}
                    isDark={isDark}
                  />
                  <StatBox
                    label={t("welcome.stat.archive")}
                    value={counts?.history_counter ?? 0}
                    isDark={isDark}
                  />
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => setVisible(false)}
                  sx={{
                    borderRadius: 2,
                    py: 1.3,
                    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
                    boxShadow: "0 4px 14px rgba(59,130,246,0.4)",
                    "&:hover": {
                      background: `linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)`,
                      boxShadow: "0 6px 18px rgba(59,130,246,0.5)",
                    },
                  }}
                >
                  {t("welcome.enter")}
                </Button>

                {/* Last login footer */}
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: "0.63rem",
                    color: "text.disabled",
                    letterSpacing: "0.02em",
                  }}
                >
                  {t("welcome.today")} {todayFormatted}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

WelcomeSplash.propTypes = {
  onDismiss: PropTypes.func.isRequired,
};

export default WelcomeSplash;
